import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'

// evertel
import { Modal, ModalBody, ModalHeader, Row, Col, Button, Text, useUI, Icon, Badge, Tooltip, TooltipTrigger, TooltipContent } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { UserController, NO_POSITION_TEXT, useUserTagline } from '@evertel/blue-user'
import { useWindowDimensions } from '@evertel/web/hooks'
import { DepartmentsAccessController } from '@evertel/departments-access'
import { DepartmentController, DepartmentStore } from '@evertel/department'
import { CreateThreadController } from '@evertel/thread'
import { DrawerController } from '@evertel/message'
import { partitionDepartmentsForProfile, getRole, DepartmentAccess } from '@evertel/utils'

interface UserProfileModalProps {
    visible: boolean
    userId: number
    onClose: () => void
    onCloseDone?: () => void
}

const UserProfileModal: React.FC<UserProfileModalProps> = observer(({
    visible,
    userId,
    onClose,
    onCloseDone
}) => {

    const navigate = useNavigate()
    const { addToast } = useUI()
    const { height: windowHeight } = useWindowDimensions()
    const sessionService = useService(SessionService, [])
    const userController = useService(UserController, [])
    const departmentsAccessController = useService(DepartmentsAccessController, [])
    const departmentController = useService(DepartmentController, [])
    const departmentStore = useService(DepartmentStore, [])
    const createThreadController = useService(CreateThreadController, [])
    const drawerController = useService(DrawerController, [])

    const [isLoading, setIsLoading] = useState(false)
    const [isStartingThread, setIsStartingThread] = useState(false)

    const { displayPosition } = useUserTagline({
        departmentsAccess: departmentsAccessController.departmentsAccess,
        contextDepartmentId: sessionService.selectedDepartmentId,
        reportingDepartment: userController.user?.reportingDepartment,
        userId: userController.user?.id,
        isBot: userController.user?.isBot
    })

    useEffect(() => {
        (async () => {
            if (!userId) return

            setIsLoading(true)

            await Promise.all([
                userController.init(userId),
                departmentsAccessController.init(userId)
            ])

            // Hydrate department store with all departments for this user
            const deptIds = departmentsAccessController.departmentsAccess.map(da => da.departmentId).filter((id)=> id !== undefined)
            await departmentController.hydrateDepartmentsById(deptIds)

            // Handle bot case
            if (userController.user?.isBot && sessionService.selectedDepartmentId) {
                await departmentController.hydrateDepartmentsById([sessionService.selectedDepartmentId])
            }

            setIsLoading(false)
        })()
    }, [userId])

    const startThread = async () => {
        if (!userId) return

        // start the thread
        try {
            setIsStartingThread(true)
            const thread = await createThreadController.createThread(userId)
            await drawerController.fetchActiveThreads()

            onClose()

            // navigate to the new thread
            navigate(`/thread/${thread.id}`)
        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error.message
            })
        } finally {
            setIsStartingThread(false)
        }
    }

    const editProfile = () => {
        onClose()
        navigate(`/account/profile/${userId}`)
    }

    const user = userController.user
    const isCurrentUser = (user?.id === sessionService.currentUserId)
    const { primary, selected, nonGuest, guest } = partitionDepartmentsForProfile(
        departmentsAccessController.departmentsAccess,
        sessionService.selectedDepartmentId
    )

    const renderDepartmentCard = (da: DepartmentAccess, prominent = false) => {
        const dept = departmentStore.findById(da.departmentId as number)
        const deptName = dept?.name || 'Unknown Agency'
        const role = getRole(da.roles)
        const isGuest = role === 'guest'
        const isPrimary = da.departmentId === primary?.departmentId

        return (
            <div
                key={da.departmentId}
                className={prominent ? 'col-12 col-sm-6 mb-3' : 'col-12 col-sm-6 col-lg-4 mb-3'}
            >
                <div className={`p-${prominent ? 3 : 2} department-box rounded h-100`}>
                    <div className="d-flex align-items-center flex-wrap mb-1">
                        <Text color="primary" tag={prominent ? 'h5' : 'h6'} bold className="mb-0 me-2">
                            {deptName}
                        </Text>
                        {isPrimary && (
                            <Badge color="primary" className="me-1">Primary</Badge>
                        )}
                        {isGuest && (
                            <Badge color="warning">Guest</Badge>
                        )}
                    </div>
                    <Text size={prominent ? undefined : 'small'} color="muted" style={{ wordBreak: 'break-word' }}>
                        {da.positionString || NO_POSITION_TEXT}
                    </Text>
                </div>
            </div>
        )
    }

    const isGuestOnly = departmentsAccessController.departmentsAccess?.length > 0
        && departmentsAccessController.departmentsAccess.every(da => getRole(da.roles) === 'guest')
    const reportingDepartment = user?.reportingDepartment
    const showReportingAgency = isGuestOnly && reportingDepartment?.department

    const renderReportingAgencyCard = () => {
        if (!reportingDepartment?.department) return null

        return (
            <div className="col-12 col-sm-6 mb-3">
                <div className="p-3 department-box rounded h-100">
                    <div className="d-flex align-items-center flex-wrap mb-1">
                        <Text color="primary" tag="h5" bold className="mb-0 me-2">
                            {reportingDepartment.department}
                        </Text>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="d-inline-flex align-items-center">
                                    <Badge color="warning">
                                        <Icon
                                            name="circle-exclamation"
                                            className="me-1"
                                            size={12}
                                        />
                                        Reporting Agency
                                    </Badge>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>This agency was self reported by this user</TooltipContent>
                        </Tooltip>
                    </div>
                    <Text color="muted" style={{ wordBreak: 'break-word' }}>
                        {reportingDepartment.position || NO_POSITION_TEXT}
                    </Text>
                </div>
            </div>
        )
    }

    const renderProminentDepartments = () => {
        if (user?.isBot) {
            const dept = departmentStore.findById(sessionService.selectedDepartmentId)
            return (
                <div className={`p-3 department-box rounded h-100`}>
                    <Text tag="h3" bold>{dept?.name || 'Unknown Department'}</Text>
                    <Text tag="h5">{displayPosition}</Text>
                </div>
            )
        }

        if (!departmentsAccessController.departmentsAccess?.length) {
            return <Text tag="h5" italic>No agencies</Text>
        }

        return (
            <div className="row">
                {showReportingAgency && renderReportingAgencyCard()}
                {primary && renderDepartmentCard(primary, true)}
                {selected && renderDepartmentCard(selected, true)}
            </div>
        )
    }

    const renderRemainingDepartments = () => {
        if (user?.isAdmin) return null
        if (nonGuest.length === 0 && guest.length === 0) return null

        return (
            <>
                <Text size="small" color="muted" className="mt-3 mb-2">Other Agencies</Text>
                {/* Non-guest departments */}
                {nonGuest.length > 0 && (
                    <div className="row">
                        {nonGuest.map(da => renderDepartmentCard(da))}
                    </div>
                )}

                {/* Guest departments */}
                {guest.length > 0 && (
                    <>
                        {nonGuest.length > 0 && <hr className="my-2" />}
                        <div className="row">
                            {guest.map(da => renderDepartmentCard(da))}
                        </div>
                    </>
                )}
            </>
        )
    }

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            onCloseDone={onCloseDone}>
            <ModalHeader />
            <ModalBody className="p-0 pt-3">
                {/* IMAGE */}
                <Row>
                    {(user?.publicImage || user?.publicMedia) ? (
                        <img
                            src={(user.publicMedia) ? user.publicMedia.url + '?ver=sqr500' : user.publicImage}
                            alt={''}
                            width="100%"
                            style={{
                                maxHeight: windowHeight * 0.5,
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <img
                            src="/assets/img/avatars/user_photo_default.jpg"
                            alt={''}
                            width="100%"
                            style={{
                                maxHeight: windowHeight * 0.35,
                                objectFit: 'cover'
                            }}
                        />
                    )}
                </Row>
                {/* NAME */}
                <Row className="p-3">
                    <Col className="pt-3">
                        <Row>
                            <Text tag="h1">
                                {user?.firstName + ' ' + user?.lastName}
                            </Text>
                            {(user?.isAdmin) &&
                                <Icon
                                    name="star"
                                    color="warning"
                                    type="solid"
                                    className="ms-1"
                                    title="Administrator"
                                />
                            }
                        </Row>
                    </Col>
                </Row>
                {/* PROMINENT DEPARTMENTS */}
                <Row className="px-3">
                    <Col>
                        {renderProminentDepartments()}
                    </Col>
                </Row>
                {/* BUTTONS */}
                <Row className="p-3">
                    {!isCurrentUser && !user?.isBot && (
                        <>
                            <Button
                                onClick={startThread}
                                loading={isStartingThread}
                                color="primary"
                                className="me-2">
                                DM {user?.firstName}
                            </Button>
                            {user?.phoneNumber && (
                                <Button
                                    className="me-2"
                                    outline
                                    color="primary"
                                    onClick={() => window.open(`tel:${user.phoneNumber}`)}
                                    data-tooltip={user?.phoneNumber}
                                    data-tooltip-pos="top">
                                    Call {user?.firstName}
                                </Button>
                            )}
                            {user?.email && (
                                <Button
                                    outline
                                    color="primary"
                                    onClick={() => window.open(`mailto:${user.email}`)}>
                                    Email {user?.firstName}
                                </Button>
                            )}
                        </>
                    )}
                    {isCurrentUser && (
                        <Button
                            color="primary"
                            onClick={editProfile}
                        >
                            Edit Profile
                        </Button>
                    )}
                </Row>
                {/* REMAINING DEPARTMENTS */}
                <Row className="px-3 mb-3">
                    <Col>
                        {renderRemainingDepartments()}
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
})

export { UserProfileModal }
